import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useFarm } from '../context/FarmContext';
import { io } from 'socket.io-client';
import { functionDefs } from '../particleFunctions';
import { authFetch } from '../utils/authFetch';
import { canRestartControllerForDevice } from '../utils/roles';
import { isRateLimitBackoffActive } from '../utils/rateLimitBackoff';
import { emitJoinFarmRooms, subscribeSocketFarmRooms } from '../utils/farmSocketRooms';
import { getSocketIoServerUrl, SOCKET_IO_CLIENT_OPTIONS } from '../utils/socketIoClientOptions';
import { getCalibrationSecondsLeft } from '../utils/calibrationCountdown';
import { buildClimateThresholdState, serializeClimateThresholds } from '../utils/climateThresholds';
import {
  RELAY_PENDING_TIMEOUT_MS,
  RELAY_SOCKET_MAP,
  RELAY_MODE_MANUAL_ON,
  RELAY_MODE_MANUAL_OFF,
  normalizeRelayMode,
  normalizeRelayMap,
} from '../utils/deviceRelay';
import { parseTimestamp } from '../utils/timeUtils';
import ClimateCharts from './ClimateCharts';
import DeviceSettingsModal from './DeviceSettingsModal';
import './DeviceCard.css';

const LEGACY_POLL_INTERVAL_MS = 5 * 60 * 1000;
const LIVE_PULSE_MS = 1200;
let sharedDeviceSocket = null;

function particleDeviceIdsEqual(a, b) {
  const s = String(a || '').trim();
  const t = String(b || '').trim();
  if (!s || !t) return false;
  if (s === t) return true;
  if (/^[a-f0-9]{24}$/i.test(s) && /^[a-f0-9]{24}$/i.test(t)) return s.toLowerCase() === t.toLowerCase();
  return false;
}

function getSharedDeviceSocket() {
  if (!sharedDeviceSocket) {
    sharedDeviceSocket = io(getSocketIoServerUrl(), SOCKET_IO_CLIENT_OPTIONS);
    subscribeSocketFarmRooms(sharedDeviceSocket);
  }
  return sharedDeviceSocket;
}

function buildParsedReadingsFromSocket(payload = {}, fallback = {}) {
  const next = { ...(fallback || {}) };
  if (payload.co2 != null) next.co2 = `${payload.co2} ppm`;
  if (payload.temperature != null) next.temperature = `${payload.temperature} °C`;
  if (payload.humidity != null) next.humidity = `${payload.humidity} %`;
  if (payload.vpd != null) next.vpd = `${payload.vpd} kPa`;
  if (payload.light != null) next.light = payload.light;
  if (payload.lightStatus != null) next.lightStatus = payload.lightStatus;
  return next;
}

function hasRealTelemetryPayload(payload = {}) {
  if (payload?.latestTelemetry) return true;
  if (
    [
      'temperature',
      'humidity',
      'co2',
      'vpd',
      'ph',
      'ec',
      'orp',
      'level',
      'volume',
      'rA',
      'rD',
      'rC',
      'rL',
      'rb',
    ].some((key) => payload?.[key] !== undefined && payload?.[key] !== null)
  ) {
    return true;
  }
  return hasRelayPayload(payload);
}

function hasRelayPayload(source = {}) {
  if (!source || typeof source !== 'object') return false;
  if (Array.isArray(source?.control?.outlets)) {
    const outletByKey = Object.fromEntries(source.control.outlets.map((outlet) => [outlet?.key, outlet?.state]));
    if (
      ['ac', 'dryer', 'co2', 'extra'].some(
        (key) => outletByKey[key] !== undefined && outletByKey[key] !== null && outletByKey[key] !== ''
      )
    ) {
      return true;
    }
  }
  if (source.relays && typeof source.relays === 'object') {
    return ['ac', 'dryer', 'co2', 'extra', 'rA', 'rD', 'rC', 'rL'].some(
      (key) => source.relays[key] !== undefined && source.relays[key] !== null
    );
  }
  if (['rA', 'rD', 'rC', 'rL'].some((key) => source[key] !== undefined && source[key] !== null)) return true;
  if (source.socketStates && typeof source.socketStates === 'object') {
    return ['temperature', 'humidity', 'co2', 'reserve'].some(
      (key) => source.socketStates[key] !== undefined && source.socketStates[key] !== null
    );
  }
  if (source.sockets && typeof source.sockets === 'object') {
    return ['temperature', 'humidity', 'co2', 'reserve'].some(
      (key) => source.sockets[key] !== undefined && source.sockets[key] !== null
    );
  }
  return false;
}

function hasKnownRelayState(relays = {}) {
  return ['ac', 'dryer', 'co2', 'extra'].some((key) => relays[key] !== null && relays[key] !== undefined && relays[key] !== '');
}

function mergeRelaySnapshots(base = {}, incoming = {}) {
  return {
    ac: incoming?.ac ?? base?.ac ?? null,
    dryer: incoming?.dryer ?? base?.dryer ?? null,
    co2: incoming?.co2 ?? base?.co2 ?? null,
    extra: incoming?.extra ?? base?.extra ?? null,
  };
}

export default function DeviceCard({
  device,
  t,
  lang,
  formatDate,
  onRefresh,
  onFileChange,
  onUpload,
  firmwareFile,
  uploading,
  successMsg,
  showFarmTransfer = false,
  allowFarmUnassign = false,
  farmsList = [],
  onDeviceFarmMoved,
}) {
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState('');
  const [values, setValues] = useState({});
  const [disabledFns, setDisabledFns] = useState({});
  const [msg, setMsg] = useState(null);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportType, setExportType] = useState('');
  const [exportRange, setExportRange] = useState({ from: '', to: '' });
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const downloadRef = useRef(null);
  const toDisplay = (s) => (s ? s.replace(/_/g, ' ') : s);
  const toSend = (s) => (s ? s.replace(/\s+/g, '_') : s);

  const [name, setName] = useState(toDisplay(device.customName || device.name));
  const [editingName, setEditingName] = useState(false);
  const [tempName, setTempName] = useState('');
  const [nameError, setNameError] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [pendingFreshData, setPendingFreshData] = useState(false);
  const [telegramEnabled, setTelegramEnabled] = useState(device.telegramEnabled ?? device.alertsEnabled !== false);
  const [powerAlerts, setPowerAlerts] = useState(device.powerAlerts ?? device.notifyOnOffline ?? true);
  const [thresholdAlerts, setThresholdAlerts] = useState(device.thresholdAlerts ?? device.alertsEnabled !== false);
  const [trendAlerts, setTrendAlerts] = useState(device.trendAlerts ?? device.trendAlertsEnabled ?? false);
  const [initialAlertSettings, setInitialAlertSettings] = useState({
    telegramEnabled: device.telegramEnabled ?? device.alertsEnabled !== false,
    powerAlerts: device.powerAlerts ?? device.notifyOnOffline ?? true,
    thresholdAlerts: device.thresholdAlerts ?? device.alertsEnabled !== false,
    trendAlerts: device.trendAlerts ?? device.trendAlertsEnabled ?? false,
  });
  const [notificationDelivery, setNotificationDelivery] = useState(device.notificationDelivery || null);
  const [notificationActionPending, setNotificationActionPending] = useState(false);
  const [settingsThresholds, setSettingsThresholds] = useState(buildClimateThresholdState(device.thresholds));
  const [initialSettingsThresholds, setInitialSettingsThresholds] = useState(buildClimateThresholdState(device.thresholds));
  const [systemTime, setSystemTime] = useState(null);
  const [co2OffsetDraft, setCo2OffsetDraft] = useState('0');
  const [calibrationTargetPpm, setCalibrationTargetPpm] = useState('400');
  const [timeSyncPending, setTimeSyncPending] = useState(false);
  const [settingsStatus, setSettingsStatus] = useState(null);
  const [settingsProgressText, setSettingsProgressText] = useState('');
  const [liveStatus, setLiveStatus] = useState(device.status);
  const [socketOverride, setSocketOverride] = useState(null);
  const [relayPending, setRelayPending] = useState({});
  const [relayExpected, setRelayExpected] = useState({});
  const [relayCommandStatus, setRelayCommandStatus] = useState(null);
  const [relaySnapshot, setRelaySnapshot] = useState(() => normalizeRelayMap(device || {}));
  const { token: authToken, user } = useAuth();
  const { effectiveFarmId, contextOverrideId } = useFarm();

  const [relayModes, setRelayModes] = useState(() => ({
    ac: normalizeRelayMode(device?.meta?.relayModes?.ac),
    dryer: normalizeRelayMode(device?.meta?.relayModes?.dryer),
    co2: normalizeRelayMode(device?.meta?.relayModes?.co2),
    extra: normalizeRelayMode(device?.meta?.relayModes?.extra),
  }));
  const [livePulse, setLivePulse] = useState(false);
  const [farmTransferValue, setFarmTransferValue] = useState(() =>
    device.farmId != null && device.farmId !== '' ? String(device.farmId) : ''
  );
  const [farmTransferSaving, setFarmTransferSaving] = useState(false);
  const [farmTransferMsg, setFarmTransferMsg] = useState(null);
  const [nameSavingModal, setNameSavingModal] = useState(false);
  const [calibrationCountdownSec, setCalibrationCountdownSec] = useState(0);
  const [calibrationStartedUi, setCalibrationStartedUi] = useState(false);
  const [restartPending, setRestartPending] = useState(false);
  const [restartStatus, setRestartStatus] = useState(null);

  const hasThresholdChanges = useMemo(() => {
    const current = serializeClimateThresholds(settingsThresholds);
    const initial = serializeClimateThresholds(initialSettingsThresholds);
    const hasAlertChanges =
      telegramEnabled !== initialAlertSettings.telegramEnabled ||
      powerAlerts !== initialAlertSettings.powerAlerts ||
      thresholdAlerts !== initialAlertSettings.thresholdAlerts ||
      trendAlerts !== initialAlertSettings.trendAlerts;
    return JSON.stringify(current) !== JSON.stringify(initial) || hasAlertChanges;
  }, [
    settingsThresholds,
    initialSettingsThresholds,
    telegramEnabled,
    powerAlerts,
    thresholdAlerts,
    trendAlerts,
    initialAlertSettings,
  ]);
  const deviceIdRef = useRef(device.id);
  const onRefreshRef = useRef(onRefresh);
  const mqttEnabledRef = useRef(device.mqttEnabled === true || device.meta?.mqttEnabled === true);
  const livePulseTimerRef = useRef(null);
  const restartStatusTimerRef = useRef(null);
  const relayExpectedRef = useRef({});
  const relayPendingTimersRef = useRef({});
  const dir = lang === 'he' ? 'rtl' : 'ltr';
  const tx = (key) => t(key);
  const saveFailedText = t('saveFailed');

  const toLocalInputValue = (date) => {
    if (!date) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(
      date.getHours()
    )}:${pad(date.getMinutes())}`;
  };

  useEffect(() => {
    if (device.customName || device.name) {
      setName(toDisplay(device.customName || device.name));
      return;
    }
    authFetch(`/api/device/${device.id}/status`, {}, { token: localStorage.getItem('token') })
      .then((r) => r.json())
      .then((d) => d.name && setName(toDisplay(d.name)))
      .catch(() => {});
  }, [device.id, device.name, device.customName]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (downloadRef.current && !downloadRef.current.contains(event.target)) {
        setDownloadOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    const nextAlertSettings = {
      telegramEnabled: device.telegramEnabled ?? device.alertsEnabled !== false,
      powerAlerts: device.powerAlerts ?? device.notifyOnOffline ?? true,
      thresholdAlerts: device.thresholdAlerts ?? device.alertsEnabled !== false,
      trendAlerts: device.trendAlerts ?? device.trendAlertsEnabled ?? false,
    };
    setTelegramEnabled(nextAlertSettings.telegramEnabled);
    setPowerAlerts(nextAlertSettings.powerAlerts);
    setThresholdAlerts(nextAlertSettings.thresholdAlerts);
    setTrendAlerts(nextAlertSettings.trendAlerts);
    setInitialAlertSettings(nextAlertSettings);
    setNotificationDelivery(device.notificationDelivery || null);
    setSettingsThresholds(buildClimateThresholdState(device.thresholds));
      setInitialSettingsThresholds(buildClimateThresholdState(device.thresholds));
    setLiveStatus(device.status);
    setSocketOverride(null);
    setRelayPending({});
    setRelayExpected({});
    setRelayCommandStatus(null);
    setRestartStatus(null);
    if (restartStatusTimerRef.current) {
      clearTimeout(restartStatusTimerRef.current);
      restartStatusTimerRef.current = null;
    }
    Object.values(relayPendingTimersRef.current).forEach((timerId) => clearTimeout(timerId));
    relayPendingTimersRef.current = {};
    if (hasRelayPayload(device)) {
      setRelaySnapshot(normalizeRelayMap(device));
    }
    setRelayModes({
      ac: normalizeRelayMode(device?.meta?.relayModes?.ac),
      dryer: normalizeRelayMode(device?.meta?.relayModes?.dryer),
      co2: normalizeRelayMode(device?.meta?.relayModes?.co2),
      extra: normalizeRelayMode(device?.meta?.relayModes?.extra),
    });
  }, [device]);

  useEffect(() => {
    setFarmTransferValue(device.farmId != null && device.farmId !== '' ? String(device.farmId) : '');
  }, [device.id, device.farmId]);

  useEffect(() => {
    deviceIdRef.current = device.id;
  }, [device.id]);

  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  useEffect(() => {
    mqttEnabledRef.current = device.mqttEnabled === true || device.meta?.mqttEnabled === true;
  }, [device.meta?.mqttEnabled, device.mqttEnabled]);

  useEffect(() => {
    relayExpectedRef.current = relayExpected;
  }, [relayExpected]);

  useEffect(() => () => {
    if (livePulseTimerRef.current) {
      clearTimeout(livePulseTimerRef.current);
    }
    if (restartStatusTimerRef.current) {
      clearTimeout(restartStatusTimerRef.current);
    }
    Object.values(relayPendingTimersRef.current).forEach((timerId) => clearTimeout(timerId));
    relayPendingTimersRef.current = {};
  }, []);

  const triggerLivePulse = () => {
    setLivePulse(true);
    if (livePulseTimerRef.current) clearTimeout(livePulseTimerRef.current);
    livePulseTimerRef.current = setTimeout(() => {
      setLivePulse(false);
      livePulseTimerRef.current = null;
    }, LIVE_PULSE_MS);
  };

  useEffect(() => {
    emitJoinFarmRooms(getSharedDeviceSocket());
  }, [authToken, effectiveFarmId, contextOverrideId]);

  useEffect(() => {
    const socket = getSharedDeviceSocket();
    const localId = String(device.deviceId || device.id || '');
    if (!localId) return undefined;

    const handleDeviceData = (parsedData) => {
      const incomingId = String(parsedData?.deviceId || parsedData?.id || '');
      if (!incomingId || !particleDeviceIdsEqual(incomingId, localId)) return;
      setLiveStatus('online');
      const telemetryTimestamp = hasRealTelemetryPayload(parsedData)
        ? parsedData?.latestTelemetry?.timestamp || parsedData?.timestamp || new Date().toISOString()
        : null;
      setSocketOverride((prev) => ({
        ...(prev || {}),
        status: 'online',
        isOnline: true,
        ...(telemetryTimestamp
          ? {
              lastUpdated: telemetryTimestamp,
              timestamp: telemetryTimestamp,
              lastTelemetryAt: telemetryTimestamp,
              latestTelemetryAt: telemetryTimestamp,
              lastRealSensorUpdateAt: telemetryTimestamp,
            }
          : {}),
        latestTelemetry: parsedData?.latestTelemetry || prev?.latestTelemetry || null,
        deviceStatus: {
          ...(prev?.deviceStatus || {}),
          ...(parsedData?.deviceStatus || {}),
          status: parsedData?.deviceStatus?.status || 'online',
          lastSeenAt: parsedData?.deviceStatus?.lastSeenAt || new Date().toISOString(),
        },
        dayStatus: parsedData?.dayStatus ?? prev?.dayStatus,
        preset: parsedData?.preset ?? prev?.preset,
        co2Warning: parsedData?.co2Warning ?? prev?.co2Warning ?? null,
        co2: parsedData?.co2 ?? prev?.co2,
        temperature: parsedData?.temperature ?? prev?.temperature,
        humidity: parsedData?.humidity ?? prev?.humidity,
        vpd: parsedData?.vpd ?? prev?.vpd,
        ph: parsedData?.ph ?? prev?.ph,
        ec: parsedData?.ec ?? prev?.ec,
        orp: parsedData?.orp ?? prev?.orp,
        temp: parsedData?.temp ?? prev?.temp,
        ecTemp: parsedData?.ecTemp ?? prev?.ecTemp,
        level: parsedData?.level ?? prev?.level,
        volume: parsedData?.volume ?? prev?.volume,
        tankMaxHeight: parsedData?.tankMaxHeight ?? prev?.tankMaxHeight,
        tankFullVolume: parsedData?.tankFullVolume ?? prev?.tankFullVolume,
        light: parsedData?.light ?? prev?.light,
        lightStatus: parsedData?.lightStatus ?? prev?.lightStatus,
        rA: parsedData?.rA ?? prev?.rA,
        rD: parsedData?.rD ?? prev?.rD,
        rC: parsedData?.rC ?? prev?.rC,
        rL: parsedData?.rL ?? prev?.rL,
        rb: parsedData?.rb ?? prev?.rb,
        relays: parsedData?.relays ?? prev?.relays,
        relayModes: {
          ...(prev?.relayModes || {}),
          ...(parsedData?.relayModes || parsedData?.meta?.relayModes || {}),
        },
        control: parsedData?.control
          ? {
              ...(prev?.control || {}),
              ...parsedData.control,
            }
          : prev?.control,
        socketStates: {
          ...(prev?.socketStates || {}),
          ...(parsedData?.socketStates || {}),
        },
        deviceStatuses: {
          ...(prev?.deviceStatuses || {}),
          ...(parsedData?.deviceStatuses || {}),
        },
        parsedReadings: buildParsedReadingsFromSocket(
          parsedData,
          prev?.parsedReadings || device.parsedReadings || {}
        ),
      }));
      if (hasRelayPayload(parsedData)) {
        const nextRelaySnapshot = normalizeRelayMap(parsedData);
        const confirmedRelayKeys = Object.entries(relayExpectedRef.current)
          .filter(([relayKey, expectedValue]) => {
            const current = Number(nextRelaySnapshot?.[relayKey]);
            if (!Number.isFinite(current)) return false;
            return expectedValue === 'any' ? true : current === Number(expectedValue);
          })
          .map(([relayKey]) => relayKey);
        confirmedRelayKeys.forEach((relayKey) => {
          if (relayPendingTimersRef.current[relayKey]) {
            clearTimeout(relayPendingTimersRef.current[relayKey]);
            delete relayPendingTimersRef.current[relayKey];
          }
        });
        setRelaySnapshot((prev) =>
          mergeRelaySnapshots(
            mergeRelaySnapshots(normalizeRelayMap(device || {}), prev || {}),
            nextRelaySnapshot
          )
        );
        setRelayPending((prev) => {
          if (!Object.keys(prev).length) return prev;
          const next = { ...prev };
          confirmedRelayKeys.forEach((relayKey) => {
            next[relayKey] = false;
          });
          return next;
        });
        setRelayExpected((prev) => {
          const next = { ...prev };
          confirmedRelayKeys.forEach((relayKey) => {
            if (next[relayKey] !== undefined) {
              delete next[relayKey];
            }
          });
          return next;
        });
        if (confirmedRelayKeys.length) {
          setRelayCommandStatus(null);
        }
      }
      if (parsedData?.relayModes || parsedData?.meta?.relayModes) {
        const nextModes = parsedData?.relayModes || parsedData?.meta?.relayModes || {};
        setRelayModes((prev) => ({
          ...prev,
          ...(nextModes.ac ? { ac: normalizeRelayMode(nextModes.ac) } : {}),
          ...(nextModes.dryer ? { dryer: normalizeRelayMode(nextModes.dryer) } : {}),
          ...(nextModes.co2 ? { co2: normalizeRelayMode(nextModes.co2) } : {}),
          ...(nextModes.extra ? { extra: normalizeRelayMode(nextModes.extra) } : {}),
        }));
      }
      triggerLivePulse();
    };

    const handleDeviceStatus = (statusData) => {
      const incomingId = String(statusData?.deviceId || statusData?.id || '');
      if (!incomingId || !particleDeviceIdsEqual(incomingId, localId)) return;
      const nextStatus = String(statusData?.status || '').toLowerCase();
      if (nextStatus === 'offline') {
        setLiveStatus('offline');
        setSocketOverride((prev) => ({
          ...(prev || {}),
          status: 'offline',
          isOnline: false,
          deviceStatus: {
            ...(prev?.deviceStatus || {}),
            status: 'offline',
            lastSeenAt: statusData?.timestamp || prev?.deviceStatus?.lastSeenAt || null,
          },
        }));
      } else if (nextStatus === 'online') {
        setLiveStatus('online');
        setSocketOverride((prev) => ({
          ...(prev || {}),
          status: 'online',
          isOnline: true,
          deviceStatus: {
            ...(prev?.deviceStatus || {}),
            status: 'online',
            lastSeenAt: statusData?.timestamp || new Date().toISOString(),
          },
        }));
      }
      triggerLivePulse();
    };

    const handleDeviceConfigUpdated = (eventPayload) => {
      const incomingId = String(eventPayload?.deviceId || eventPayload?.id || '');
      if (!incomingId || !particleDeviceIdsEqual(incomingId, localId)) return;
      const nextConfig = eventPayload?.config && typeof eventPayload.config === 'object' ? eventPayload.config : {};
      if (typeof nextConfig.telegramEnabled === 'boolean') {
        setTelegramEnabled(nextConfig.telegramEnabled);
      }
      if (typeof nextConfig.powerAlerts === 'boolean') {
        setPowerAlerts(nextConfig.powerAlerts);
      }
      if (typeof nextConfig.thresholdAlerts === 'boolean') {
        setThresholdAlerts(nextConfig.thresholdAlerts);
      }
      if (typeof nextConfig.trendAlerts === 'boolean') {
        setTrendAlerts(nextConfig.trendAlerts);
      }
      if (nextConfig.thresholds && typeof nextConfig.thresholds === 'object') {
        setSettingsThresholds(buildClimateThresholdState(nextConfig.thresholds));
      }
      setSocketOverride((prev) => ({
        ...(prev || {}),
        thresholds:
          nextConfig.thresholds && typeof nextConfig.thresholds === 'object'
            ? { ...(prev?.thresholds || {}), ...nextConfig.thresholds }
            : prev?.thresholds,
      }));
      triggerLivePulse();
    };

    socket.on('device_data', handleDeviceData);
    socket.on('device_status', handleDeviceStatus);
    socket.on('device_config_updated', handleDeviceConfigUpdated);
    return () => {
      socket.off('device_data', handleDeviceData);
      socket.off('device_status', handleDeviceStatus);
      socket.off('device_config_updated', handleDeviceConfigUpdated);
    };
  }, [device.deviceId, device.id, device.parsedReadings]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      if (isRateLimitBackoffActive()) return;
      if (mqttEnabledRef.current) return;
      onRefreshRef.current?.(deviceIdRef.current);
    }, LEGACY_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  const displayDevice = useMemo(() => {
    const resolvedRelays = hasKnownRelayState(relaySnapshot)
      ? relaySnapshot
      : socketOverride?.relays || device.relays || device.meta?.relays || {};
    if (!socketOverride) {
      return {
        ...device,
        status: liveStatus || device.status,
        relays: resolvedRelays,
        deviceStatus: {
          ...(device.deviceStatus || {}),
          ...(liveStatus ? { status: liveStatus } : {}),
        },
      };
    }
    return {
      ...device,
      ...socketOverride,
      status: liveStatus || socketOverride.status || device.status,
      latestTelemetry: socketOverride.latestTelemetry || device.latestTelemetry || null,
      lastTelemetryAt: socketOverride.lastTelemetryAt || device.lastTelemetryAt || null,
      latestTelemetryAt: socketOverride.latestTelemetryAt || device.latestTelemetryAt || null,
      lastRealSensorUpdateAt: socketOverride.lastRealSensorUpdateAt || device.lastRealSensorUpdateAt || null,
      deviceStatus: {
        ...(device.deviceStatus || {}),
        ...(socketOverride.deviceStatus || {}),
        ...(liveStatus ? { status: liveStatus } : {}),
      },
      parsedReadings: {
        ...(device.parsedReadings || {}),
        ...(socketOverride.parsedReadings || {}),
      },
      socketStates: {
        ...(device.socketStates || {}),
        ...(socketOverride.socketStates || {}),
      },
      deviceStatuses: {
        ...(device.deviceStatuses || {}),
        ...(socketOverride.deviceStatuses || {}),
      },
      relays: resolvedRelays,
      meta: {
        ...(device.meta || {}),
        ...(socketOverride.meta || {}),
      },
    };
  }, [device, liveStatus, relaySnapshot, socketOverride]);
  const connectionState = useMemo(() => {
    const status = String(displayDevice?.deviceStatus?.status || displayDevice?.status || '').toLowerCase();
    if (status === 'offline') return 'offline';
    if (status === 'online') return 'online';
    if (status === 'idle') return 'delayed';
    const ts =
      displayDevice?.deviceStatus?.lastSeenAt ||
      displayDevice?.lastSeenAtIso ||
      displayDevice?.lastSeenAt;
    if (!ts) return 'offline';
    const ageMs = Date.now() - parseTimestamp(ts).getTime();
    if (!Number.isFinite(ageMs)) return 'offline';
    if (ageMs <= 5 * 60 * 1000) return 'online';
    if (ageMs <= 15 * 60 * 1000) return 'delayed';
    return 'offline';
  }, [
    displayDevice?.deviceStatus?.lastSeenAt,
    displayDevice?.deviceStatus?.status,
    displayDevice?.lastSeenAt,
    displayDevice?.lastSeenAtIso,
    displayDevice?.status,
  ]);
  const relayControlsDisabled = connectionState === 'offline';
  const relayVisualStale = connectionState !== 'online';
  const canRestartController = canRestartControllerForDevice(user, device);

  useEffect(() => {
    const pending = Boolean(displayDevice?.pendingCalibration);
    const requestedAt = displayDevice?.calibrationRequestedAt;
    const initialLeft = getCalibrationSecondsLeft(pending, requestedAt);
    if (initialLeft <= 0) {
      setCalibrationStartedUi(false);
      setCalibrationCountdownSec(0);
      return undefined;
    }
    let timerId;
    const tick = () => {
      const left = getCalibrationSecondsLeft(pending, requestedAt);
      if (left <= 0) {
        setCalibrationStartedUi(false);
        setCalibrationCountdownSec(0);
        if (timerId != null) window.clearInterval(timerId);
        return;
      }
      setCalibrationStartedUi(true);
      setCalibrationCountdownSec(left);
    };
    tick();
    timerId = window.setInterval(tick, 1000);
    return () => {
      if (timerId != null) window.clearInterval(timerId);
    };
  }, [displayDevice?.pendingCalibration, displayDevice?.calibrationRequestedAt]);

  const safeField = (val) => {
    if (val === undefined || val === null) return t('noData');
    if (Array.isArray(val)) return val.join(' - ');
    return val;
  };
  const toMetricValue = (value) => {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : undefined;
  };
  const readings = {
    temperature: toMetricValue(displayDevice?.readings?.temperature ?? displayDevice?.temperature),
    humidity: toMetricValue(displayDevice?.readings?.humidity ?? displayDevice?.humidity),
    vpd: toMetricValue(displayDevice?.readings?.vpd ?? displayDevice?.vpd),
  };
  const latestRelayStatuses = normalizeRelayMap(displayDevice);

  const { thresholds = {} } = displayDevice;
  const dayThresholds = thresholds.day || {};
  const nightThresholds = thresholds.night || {};
  const dayNight = displayDevice.dayStatus && displayDevice.dayStatus.toLowerCase().includes('day') ? 'day' : 'night';

  const isRange = (s) => /^\d+(?:\.\d+)?\s*-\s*\d+(?:\.\d+)?$/.test(s);
  const fmtMetric = (val, unit) => {
    if (val === null || val === undefined) return t('noData');
    if (Array.isArray(val)) return `${val.join(' - ')} ${unit}`.trim();
    if (typeof val === 'string') {
      const up = val.toUpperCase();
      return isRange(val) || up === 'OFF' || up === 'ON' ? val : `${val} ${unit}`.trim();
    }
    return `${val} ${unit}`.trim();
  };
  const handleRefresh = async () => {
    if (!onRefresh) return;
    setLoading(true);
    try {
      await onRefresh(device.id);
    } finally {
      setLoading(false);
    }
  };


  const handleChange = (name, key, val) => {
    setValues((p) => ({ ...p, [name]: { ...(p[name] || {}), [key]: val } }));
  };

  const startEditName = (e) => {
    e.stopPropagation();
    setTempName(name);
    setNameError('');
    setEditingName(true);
  };

  const loadDeviceSettings = async () => {
    try {
      const response = await authFetch(
        `/api/devices/${device.id}`,
        {},
        {
          token: localStorage.getItem('token'),
          ...(device.farmId ? { farmContextId: String(device.farmId) } : {}),
        }
      );
      if (!response.ok) return;
      const data = await response.json();
      const nextAlertSettings = {
        telegramEnabled: data.telegramEnabled ?? data.alertsEnabled !== false,
        powerAlerts: data.powerAlerts ?? data.notifyOnOffline ?? true,
        thresholdAlerts: data.thresholdAlerts ?? data.alertsEnabled !== false,
        trendAlerts: data.trendAlerts ?? data.trendAlertsEnabled ?? false,
      };
      setTelegramEnabled(nextAlertSettings.telegramEnabled);
      setPowerAlerts(nextAlertSettings.powerAlerts);
      setThresholdAlerts(nextAlertSettings.thresholdAlerts);
      setTrendAlerts(nextAlertSettings.trendAlerts);
      setInitialAlertSettings(nextAlertSettings);
      setNotificationDelivery(data.notificationDelivery || null);
      const nextThresholdState = buildClimateThresholdState(data.thresholds);
      setSettingsThresholds(nextThresholdState);
      setInitialSettingsThresholds(nextThresholdState);
    } catch (_) {}
  };

  const loadSystemTime = async () => {
    try {
      const response = await authFetch(
        '/api/system/time',
        {},
        { token: localStorage.getItem('token') }
      );
      if (!response.ok) return;
      const data = await response.json();
      setSystemTime(data);
      const offset = Number.isFinite(Number(data?.co2Offset)) ? Number(data.co2Offset) : 0;
      setCo2OffsetDraft(String(offset));
    } catch (_) {}
  };

  const refreshTimeSync = async () => {
    setTimeSyncPending(true);
    try {
      await loadSystemTime();
    } finally {
      setTimeSyncPending(false);
    }
  };

  const syncTimeNow = async () => {
    setTimeSyncPending(true);
    try {
      const timezone = systemTime?.timezone || 'Asia/Jerusalem';
      const response = await authFetch(
        `/api/device/${device.id}/callFunction`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ functionName: 'syncTimeNow', arg: timezone }),
        },
        {
          token: localStorage.getItem('token'),
          ...(device.farmId ? { farmContextId: String(device.farmId) } : {}),
        }
      );
      const data = await response.json();
      if (!response.ok || Number(data?.return_value) !== 1) {
        throw new Error('syncTimeNow');
      }
      await loadSystemTime();
      setMsg({ type: 'success', text: t('save') });
    } catch (_) {
      setMsg({ type: 'error', text: t('deviceCard.syncTimeFailed') });
    } finally {
      setTimeSyncPending(false);
      setTimeout(() => setMsg(null), 3000);
    }
  };

  const saveModalDisplayName = async () => {
    const trimmed = toSend(tempName);
    if (!trimmed) {
      setMsg({ type: 'error', text: t('deviceCard.errorGeneric') });
      setTimeout(() => setMsg(null), 3000);
      return;
    }
    setNameSavingModal(true);
    try {
      const res = await authFetch(
        `/api/device/${encodeURIComponent(device.id)}/name`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: trimmed }),
        },
        {
          token: localStorage.getItem('token'),
          ...(device.farmId ? { farmContextId: String(device.farmId) } : {}),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMsg({ type: 'error', text: data.error || t('deviceCard.errorGeneric') });
        return;
      }
      if (data.name) {
        setName(toDisplay(data.name));
        setTempName(toDisplay(data.name));
      }
      setMsg({ type: 'success', text: t('save') });
      onRefresh?.(device.id);
    } catch (_) {
      setMsg({ type: 'error', text: t('deviceCard.errorGeneric') });
    } finally {
      setNameSavingModal(false);
      setTimeout(() => setMsg(null), 3000);
    }
  };

  const forceCalibrate400 = async (targetPpmRaw) => {
    setTimeSyncPending(true);
    try {
      const parsedTarget = Number(targetPpmRaw);
      const targetPpm = Number.isFinite(parsedTarget) ? Math.trunc(parsedTarget) : 400;
      const response = await authFetch(
        `/api/device/${device.id}/callFunction`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ functionName: 'calibrateCO2ppm', arg: String(targetPpm) }),
        },
        { token: localStorage.getItem('token') }
      );
      const data = await response.json();
      if (!response.ok || (data?.return_value != null && Number(data.return_value) !== 1)) {
        throw new Error('forceCalibrate');
      }
      setCalibrationStartedUi(false);
      setCalibrationCountdownSec(0);
      setMsg({ type: 'success', text: t('save') });
      onRefresh?.(device.id);
    } catch (_) {
      setMsg({ type: 'error', text: t('deviceCard.calibrationFailed') });
    } finally {
      setTimeSyncPending(false);
      setTimeout(() => setMsg(null), 3000);
    }
  };

  const saveThresholdsOnly = async () => {
    if (!hasThresholdChanges) return;
    setSavingSettings(true);
    setSettingsStatus(null);
    setSettingsProgressText(tx('settingsProgressSavingDb'));
    try {
      const climateThresholdsChanged =
        JSON.stringify(serializeClimateThresholds(settingsThresholds)) !==
        JSON.stringify(serializeClimateThresholds(initialSettingsThresholds));
      const cleanedThresholds = climateThresholdsChanged ? serializeClimateThresholds(settingsThresholds) : {};
      const response = await authFetch(
        `/api/device/${device.id}/settings`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            thresholds: cleanedThresholds,
            telegramEnabled,
            powerAlerts,
            thresholdAlerts,
            trendAlerts,
          }),
        },
        { token: localStorage.getItem('token') }
      );
      const responseData = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(responseData?.error || 'settings');
      setNotificationDelivery(responseData.notificationDelivery || notificationDelivery);

      const fallbackRequestedThresholds = {
        tempDay: cleanedThresholds.temp_day,
        humDay: cleanedThresholds.hum_day,
        co2Day: cleanedThresholds.co2_day,
        tempNight: cleanedThresholds.temp_night,
        humNight: cleanedThresholds.hum_night,
        co2Night: cleanedThresholds.co2_night,
      };
      const requestedThresholds =
        responseData?.requestedThresholds && typeof responseData.requestedThresholds === 'object'
          ? responseData.requestedThresholds
          : fallbackRequestedThresholds;
      const nextThresholdState = climateThresholdsChanged
        ? buildClimateThresholdState(requestedThresholds)
        : settingsThresholds;
      setSettingsThresholds(nextThresholdState);
      setInitialSettingsThresholds(nextThresholdState);
      setInitialAlertSettings({ telegramEnabled, powerAlerts, thresholdAlerts, trendAlerts });
      setSocketOverride((prev) => ({
        ...(prev || {}),
        telegramEnabled,
        powerAlerts,
        thresholdAlerts,
        trendAlerts,
        ...(climateThresholdsChanged
          ? {
              thresholds: {
                ...(prev?.thresholds || {}),
                ...requestedThresholds,
              },
            }
          : {}),
      }));
      setSettingsStatus({ type: 'success', text: t('deviceCard.climateParamsUpdated') });
      setMsg({ type: 'success', text: t('deviceCard.climateParamsUpdated') });
      await loadDeviceSettings();
    } catch (err) {
      const errorText =
        err?.message && err.message !== 'settings'
          ? `${t('settingsApplyFailedPrefix')}: ${err.message}`
          : saveFailedText;
      setSettingsStatus({ type: 'error', text: errorText });
      setMsg({ type: 'error', text: errorText });
    } finally {
      setSavingSettings(false);
      setSettingsProgressText('');
      setTimeout(() => setMsg(null), 3000);
    }
  };

  const enableClimateNotificationsForMe = async () => {
    setNotificationActionPending(true);
    setSettingsStatus(null);
    try {
      const response = await authFetch(
        `/api/device/${device.id}/notification-delivery/climate/enable`,
        { method: 'POST' },
        {
          token: localStorage.getItem('token'),
          ...(device.farmId ? { farmContextId: String(device.farmId) } : {}),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.error || 'settings');
      setNotificationDelivery(data.notificationDelivery || null);
      setSettingsStatus({ type: 'success', text: t('deviceSettingsModal.climateAlertsEnabledForMe') });
      await loadDeviceSettings();
    } catch (err) {
      const errorText =
        err?.message && err.message !== 'settings'
          ? `${t('settingsApplyFailedPrefix')}: ${err.message}`
          : t('deviceSettingsModal.climateAlertsEnableFailed');
      setSettingsStatus({ type: 'error', text: errorText });
    } finally {
      setNotificationActionPending(false);
    }
  };

  const handleRelayCommand = async (relayKey, mode) => {
    if (relayControlsDisabled) {
      setRelayCommandStatus({
        type: 'warning',
        text: t('deviceCard.controllerOfflineCommands'),
      });
      return;
    }
    const socketNumber = RELAY_SOCKET_MAP[relayKey];
    if (!socketNumber) return;
    const normalizedMode = normalizeRelayMode(mode);
    const expectedState =
      normalizedMode === RELAY_MODE_MANUAL_ON
        ? 1
        : normalizedMode === RELAY_MODE_MANUAL_OFF
        ? 0
        : 'any';
    setRelayPending((prev) => ({ ...prev, [relayKey]: true }));
    setRelayExpected((prev) => ({ ...prev, [relayKey]: expectedState }));
    setRelayCommandStatus(null);
    if (relayPendingTimersRef.current[relayKey]) {
      clearTimeout(relayPendingTimersRef.current[relayKey]);
    }
    relayPendingTimersRef.current[relayKey] = setTimeout(() => {
      setRelayPending((prev) => ({ ...prev, [relayKey]: false }));
      setRelayExpected((prev) => {
        const next = { ...prev };
        delete next[relayKey];
        return next;
      });
      setRelayCommandStatus({
        type: 'warning',
        text: t('deviceCard.relayCommandUnconfirmed'),
      });
      onRefreshRef.current?.(deviceIdRef.current);
      delete relayPendingTimersRef.current[relayKey];
    }, RELAY_PENDING_TIMEOUT_MS);
    if (expectedState !== 'any') {
      setRelaySnapshot((prev) =>
        mergeRelaySnapshots(
          mergeRelaySnapshots(normalizeRelayMap(device || {}), prev || {}),
          { [relayKey]: Number(expectedState) }
        )
      );
    }
    console.info('[UI][RelayCommand] click', {
      deviceId: device?.deviceId || device?.id,
      relayKey,
      requestedMode: mode,
      normalizedMode,
      socketNumber,
      expectedState,
    });
    try {
      const response = await authFetch(
        `/api/device/${device.id}/relay-command`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            socket: socketNumber,
            mode: normalizedMode,
          }),
        },
        { token: localStorage.getItem('token') }
      );
      const data = await response.json().catch(() => ({}));
      console.info('[UI][RelayCommand] api-response', {
        deviceId: device?.deviceId || device?.id,
        relayKey,
        normalizedMode,
        socketNumber,
        httpStatus: response.status,
        ok: response.ok,
        body: data,
      });
      if (!response.ok || data?.ok === false) {
        throw new Error(data?.error || 'toggleSocket');
      }
      setRelayModes((prev) => ({ ...prev, [relayKey]: normalizedMode }));
      setSocketOverride((prev) => ({
        ...(prev || {}),
        relayModes: {
          ...((prev && prev.relayModes) || {}),
          [relayKey]: normalizedMode,
        },
      }));
      if (!data?.pendingTelemetryConfirmation) {
        if (relayPendingTimersRef.current[relayKey]) {
          clearTimeout(relayPendingTimersRef.current[relayKey]);
          delete relayPendingTimersRef.current[relayKey];
        }
        setRelayPending((prev) => ({ ...prev, [relayKey]: false }));
        setRelayExpected((prev) => {
          const next = { ...prev };
          delete next[relayKey];
          return next;
        });
      }
    } catch (err) {
      console.error('[UI][RelayCommand] failed', {
        deviceId: device?.deviceId || device?.id,
        relayKey,
        normalizedMode,
        socketNumber,
        error: err?.message || err,
      });
      if (relayPendingTimersRef.current[relayKey]) {
        clearTimeout(relayPendingTimersRef.current[relayKey]);
        delete relayPendingTimersRef.current[relayKey];
      }
      setRelayPending((prev) => ({ ...prev, [relayKey]: false }));
      setRelayExpected((prev) => {
        const next = { ...prev };
        delete next[relayKey];
        return next;
      });
      const text = err?.message && err.message !== 'toggleSocket' ? err.message : t('commandError');
      setRelayCommandStatus({ type: 'danger', text });
    }
  };

  const restartController = async () => {
    setRestartPending(true);
    setRestartStatus(null);
    if (restartStatusTimerRef.current) {
      clearTimeout(restartStatusTimerRef.current);
      restartStatusTimerRef.current = null;
    }
    try {
      const response = await authFetch(
        `/api/device/${device.id}/callFunction`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ functionName: 'restartDevice', arg: '' }),
        },
        { token: authToken || localStorage.getItem('token') }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || Number(data?.return_value) !== 1) {
        const err = new Error(data?.error || 'restartDevice');
        err.code = data?.code;
        err.status = response.status;
        throw err;
      }
      const acceptedText = t('deviceSettingsModal.restartAccepted');
      setRestartStatus({ type: 'success', text: acceptedText });
      setMsg({ type: 'success', text: acceptedText });
      restartStatusTimerRef.current = setTimeout(() => {
        setRestartStatus(null);
        restartStatusTimerRef.current = null;
      }, 90000);
    } catch (err) {
      const notReachable =
        err?.code === 'DEVICE_NOT_REACHABLE' || err?.status === 503;
      const text = notReachable
        ? t('deviceSettingsModal.restartNotReachable')
        : t('deviceSettingsModal.restartFailed');
      setRestartStatus({ type: 'danger', text });
      setMsg({ type: 'error', text });
    } finally {
      setRestartPending(false);
      setTimeout(() => setMsg(null), 3000);
    }
  };

  const setThresholdField = (section, metric, part, value) => {
    setSettingsThresholds((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [metric]: {
          ...prev[section][metric],
          [part]: value,
        },
      },
    }));
  };

  const saveName = async (e) => {
    e.stopPropagation();
    setNameError('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`/api/device/${device.id}/name`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: toSend(tempName) }),
      });
      const data = await res.json();
      if (res.ok && data.name) {
        setName(toDisplay(data.name));
        setEditingName(false);
      } else {
        setNameError(data.error || t('deviceCard.errorGeneric'));
      }
    } catch (err) {
      setNameError(t('deviceCard.errorGeneric'));
    }
  };

  const cancelEdit = (e) => {
    e.stopPropagation();
    setEditingName(false);
  };

  const handleHeaderRefresh = async (event) => {
    event.stopPropagation();
    setPendingFreshData(true);
    try {
      const response = await authFetch(
        `/api/device/${device.id}/callFunction`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ functionName: 'pushNow', arg: '' }),
        },
        { token: localStorage.getItem('token') }
      );
      const data = await response.json();
      if (!response.ok || Number(data?.return_value) !== 1) {
        throw new Error('refresh');
      }
      await onRefresh?.(device.id);
      await loadDeviceSettings();
      setMsg({ type: 'success', text: t('commandSuccess') });
    } catch (_) {
      setMsg({ type: 'error', text: t('commandError') });
    } finally {
      setPendingFreshData(false);
      setTimeout(() => setMsg(null), 3000);
    }
  };

  const openExportModal = (type) => {
    const now = new Date();
    const from = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    setExportRange({
      from: toLocalInputValue(from),
      to: toLocalInputValue(now),
    });
    setExportType(type);
    setExportError('');
    setShowExportModal(true);
    setDownloadOpen(false);
  };

  const closeExportModal = () => {
    setShowExportModal(false);
    setExportType('');
    setExportError('');
  };

  const filenameFromHeader = (header, fallback) => {
    if (!header) return fallback;
    const match = header.match(/filename="?([^"]+)"?/i);
    return match ? match[1] : fallback;
  };

  const handleExportDownload = async () => {
    if (!exportType) return;
    setExporting(true);
    setExportError('');
    try {
      const params = new URLSearchParams();
      if (exportRange.from) {
        const ts = new Date(exportRange.from).getTime();
        if (!Number.isNaN(ts)) params.append('from', ts);
      }
      if (exportRange.to) {
        const ts = new Date(exportRange.to).getTime();
        if (!Number.isNaN(ts)) params.append('to', ts);
      }
      const endpoint =
        exportType === 'readings'
          ? `/api/device/${device.id}/export/readings`
          : `/api/device/${device.id}/export/settings`;
      const url = params.toString() ? `${endpoint}?${params.toString()}` : endpoint;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const fallbackName = `device_${device.id}_${exportType}_${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`;
      const filename = filenameFromHeader(
        res.headers.get('Content-Disposition'),
        fallbackName
      );
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      setShowExportModal(false);
    } catch (err) {
      console.error('Export download failed', err);
      setExportError(t('downloadFailed'));
    } finally {
      setExporting(false);
    }
  };


  const submit = (name) => {
    if (name === 'getReadings') {
      onRefresh?.(device.id);
      return;
    }
    const def = functionDefs.find((d) => d.name === name);
    if (!def) return;
    const thresholdFunctions = new Set([
      'setHumidityThreshold',
      'setTemperatureThreshold',
      'setNightHumidityThreshold',
      'setNightTemperatureThreshold',
      'setCO2Threshold',
    ]);

    let args = '';
    if (thresholdFunctions.has(name)) {
      const minRaw = String(values[name]?.min ?? '').trim();
      const maxRaw = String(values[name]?.max ?? '').trim();
      const min = Number(minRaw);
      const max = Number(maxRaw);

      if (!minRaw || !maxRaw || !Number.isFinite(min) || !Number.isFinite(max) || min > max) {
        setMsg({
          type: 'error',
          text: t('deviceCard.invalidThreshold'),
        });
        setTimeout(() => setMsg(null), 3000);
        return;
      }

      args = `${min},${max}`;
    } else {
      args = def.args
        .map((a) => {
          if (typeof a === 'string') return values[name]?.[a] || '';
          return values[name]?.[a.key] || '';
        })
        .filter((v) => v !== '')
        .join(',');
    }
    setDisabledFns((p) => ({ ...p, [name]: true }));
    fetch(`/api/device/${device.id}/callFunction`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
      body: JSON.stringify({ functionName: name, arg: args }),
    })
      .then(async (r) => {
        const data = await r.json();
        if (!r.ok && r.status === 401) {
          setMsg({ type: 'error', text: t('particleAuthError') });
          return;
        }
        const rv = parseInt(data.return_value, 10);
        if (rv === 1) {
          setMsg({ type: 'success', text: t('commandSuccess') });
          if (
            [
              'toggleSocket',
              'setHumidityThreshold',
              'setTemperatureThreshold',
              'setNightHumidityThreshold',
              'setNightTemperatureThreshold',
              'setCO2Threshold',
            ].includes(name)
          )
            onRefresh(device.id);
        } else {
          setMsg({ type: 'error', text: t('commandError') });
        }
      })
      .catch(() => setMsg({ type: 'error', text: t('commandError') }))
      .finally(() => {
        setTimeout(() => setDisabledFns((p) => ({ ...p, [name]: false })), 1000);
        setTimeout(() => setMsg(null), 3000);
      });
  };

  const renderForm = (name) => {
    const def = functionDefs.find((d) => d.name === name);
    if (!def || def.args.length === 0) return null;
    return (
      <div className="mt-2">
        {def.args.map((arg, idx) => {
          if (typeof arg === 'string') {
            return (
              <input
                key={idx}
                type="number"
                className="form-control form-control-sm mb-2"
                placeholder={t(`args.${arg}`)}
                value={values[name]?.[arg] || ''}
                onChange={(e) => handleChange(name, arg, e.target.value)}
              />
            );
          }
          if (arg.enum) {
            return (
              <select
                key={idx}
                className="form-select form-select-sm mb-2"
                value={values[name]?.[arg.key] || arg.enum[0]}
                onChange={(e) => handleChange(name, arg.key, e.target.value)}
              >
                {arg.enum.map((opt) => (
                  <option key={opt} value={opt}>
                    {t(`enums.${opt}`)}
                  </option>
                ))}
              </select>
            );
          }
          return null;
        })}
        {(() => {
          const h = t(`hints.${name}`);
          return h && h !== `hints.${name}` ? (
            <div className="form-text mb-2">{h}</div>
          ) : null;
        })()}
        <button className="btn btn-success btn-sm" onClick={() => submit(name)}>
          {t('args.submit')}
        </button>
      </div>
    );
  };

  const applyFarmTransfer = async () => {
    const current = device.farmId != null && device.farmId !== '' ? String(device.farmId) : '';
    const next = (farmTransferValue || '').trim();
    if (next === '' && !allowFarmUnassign) {
      setFarmTransferMsg({ type: 'error', text: t('farmsAdmin.farmUpdateFailed') });
      setTimeout(() => setFarmTransferMsg(null), 3000);
      return;
    }
    if (next === current) {
      setFarmTransferMsg({ type: 'muted', text: t('farmsAdmin.farmUpdateNoop') });
      setTimeout(() => setFarmTransferMsg(null), 2000);
      return;
    }
    setFarmTransferSaving(true);
    setFarmTransferMsg(null);
    try {
      const isUnbind = next === '';
      const res = await authFetch(
        isUnbind
          ? `/api/devices/unbind/${encodeURIComponent(device.id)}`
          : `/api/admin/devices/${encodeURIComponent(device.id)}/farm`,
        isUnbind
          ? { method: 'PATCH' }
          : {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ farmId: next }),
            },
        { token: localStorage.getItem('token') }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setFarmTransferMsg({ type: 'error', text: data.error || t('farmsAdmin.farmUpdateFailed') });
        return;
      }
      setFarmTransferMsg({
        type: 'success',
        text: isUnbind ? t('farmsAdmin.deviceFreed') : t('farmsAdmin.farmUpdated'),
      });
      if (isUnbind) {
        window.dispatchEvent(new Event('shefa-unassigned-refresh'));
      }
      onDeviceFarmMoved?.();
    } catch (_) {
      setFarmTransferMsg({ type: 'error', text: t('farmsAdmin.farmUpdateFailed') });
    } finally {
      setFarmTransferSaving(false);
      setTimeout(() => setFarmTransferMsg(null), 4000);
    }
  };

  const renderDownloadDropdown = () => (
    <div
      className="dropdown device-card-download"
      data-download-control
      ref={downloadRef}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        className="btn btn-sm btn-outline-success dropdown-toggle"
        type="button"
        onClick={() => setDownloadOpen((prev) => !prev)}
      >
        {t('downloadExcelReport')}
      </button>
      <div
        className={`dropdown-menu ${downloadOpen ? 'show' : ''} ${
          dir === 'rtl' ? 'dropdown-menu-end text-end' : ''
        }`}
      >
        <button
          className="dropdown-item"
          type="button"
          onClick={() => openExportModal('readings')}
        >
          {t('downloadReadings')}
        </button>
        <button
          className="dropdown-item"
          type="button"
          onClick={() => openExportModal('settings')}
        >
          {t('downloadSettingsChanges')}
        </button>
      </div>
    </div>
  );

  const calibrationCountdownText = `${Math.floor(calibrationCountdownSec / 60)
    .toString()
    .padStart(2, '0')}:${(calibrationCountdownSec % 60).toString().padStart(2, '0')}`;

  return (
    <div
      className={`shefa-device-card-root h-100 w-100 min-w-0 ${displayDevice.status === 'offline' ? 'text-muted' : ''}`}
    >
      <div className="p-0">
        <ClimateCharts
          deviceId={device.id}
          device={displayDevice}
          farmPlan={displayDevice?.farmPlan}
          latestReading={displayDevice}
          onSettings={() => {
            loadDeviceSettings();
            loadSystemTime();
            setTempName(name || '');
            setNameError('');
            setShowSettings(true);
          }}
        />
      </div>
      {showFarmTransfer &&
        device.controller !== 'water' &&
        device.controller !== 'soil' && (
          <div className="px-2 py-2 border-top bg-light bg-opacity-50">
            <label className="form-label small mb-1 text-muted">{t('farmsAdmin.moveFarmLabel')}</label>
            <div className="d-flex flex-wrap gap-2 align-items-center">
              <select
                className="form-select form-select-sm"
                style={{ maxWidth: 240 }}
                value={farmTransferValue}
                onChange={(e) => setFarmTransferValue(e.target.value)}
                disabled={farmTransferSaving}
              >
                {allowFarmUnassign ? (
                  <option value="">{t('farmsAdmin.freePoolOption')}</option>
                ) : null}
                {farmsList.map((f) => (
                  <option key={String(f._id)} value={String(f._id)}>
                    {f.name}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={applyFarmTransfer}
                disabled={farmTransferSaving}
              >
                {farmTransferSaving ? t('farmsAdmin.moving') : t('farmsAdmin.apply')}
              </button>
            </div>
            {farmTransferMsg ? (
              <div
                className={`small mt-1 ${
                  farmTransferMsg.type === 'error'
                    ? 'text-danger'
                    : farmTransferMsg.type === 'success'
                    ? 'text-success'
                    : 'text-muted'
                }`}
              >
                {farmTransferMsg.text}
              </div>
            ) : null}
            {!device.farmId ? (
              <div className="small mt-1 text-muted">{t('farmsAdmin.freeStatus')}</div>
            ) : null}
          </div>
        )}
      {showExportModal && (
        <div
          className="modal d-block"
          tabIndex="-1"
          role="dialog"
          dir={dir}
          onClick={closeExportModal}
          data-download-control
        >
          <div
            className="modal-dialog modal-dialog-centered modal-sm"
            role="document"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t('downloadExcelReport')}</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeExportModal}
                ></button>
              </div>
              <div className="modal-body">
                <div className="mb-2">
                  <label className="form-label">{t('from')}</label>
                  <input
                    type="datetime-local"
                    className="form-control"
                    value={exportRange.from}
                    onChange={(e) =>
                      setExportRange((prev) => ({ ...prev, from: e.target.value }))
                    }
                  />
                </div>
                <div className="mb-2">
                  <label className="form-label">{t('to')}</label>
                  <input
                    type="datetime-local"
                    className="form-control"
                    value={exportRange.to}
                    onChange={(e) =>
                      setExportRange((prev) => ({ ...prev, to: e.target.value }))
                    }
                  />
                </div>
                {exportError && <div className="text-danger small">{exportError}</div>}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={closeExportModal}>
                  {t('cancel')}
                </button>
                <button
                  className="btn btn-primary"
                  onClick={handleExportDownload}
                  disabled={exporting}
                >
                  {exporting ? t('loading') : t('download')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <DeviceSettingsModal
        show={showSettings}
        onClose={() => setShowSettings(false)}
        onSaveThresholds={saveThresholdsOnly}
        canSaveThresholds={hasThresholdChanges}
        saving={savingSettings}
        controller={displayDevice?.controller || 'climate'}
        t={t}
        telegramEnabled={telegramEnabled}
        setTelegramEnabled={setTelegramEnabled}
        powerAlerts={powerAlerts}
        setPowerAlerts={setPowerAlerts}
        thresholdAlerts={thresholdAlerts}
        setThresholdAlerts={setThresholdAlerts}
        trendAlerts={trendAlerts}
        setTrendAlerts={setTrendAlerts}
        thresholds={settingsThresholds}
        setThresholdField={setThresholdField}
        systemTime={systemTime}
        controllerTime={
          displayDevice.lastRealSensorUpdateAt ||
          displayDevice.latestTelemetryAt ||
          displayDevice.lastTelemetryAt ||
          displayDevice.latestTelemetry?.timestamp ||
          displayDevice.timestamp
        }
        timeSyncPending={timeSyncPending}
        co2OffsetDraft={co2OffsetDraft}
        setCo2OffsetDraft={setCo2OffsetDraft}
        calibrationTargetPpm={calibrationTargetPpm}
        setCalibrationTargetPpm={setCalibrationTargetPpm}
        onForceCalibrate={forceCalibrate400}
        pendingCalibration={Boolean(displayDevice?.pendingCalibration)}
        calibrationStartedUi={calibrationStartedUi && calibrationCountdownSec > 0}
        calibrationCountdownText={calibrationCountdownText}
        settingsStatus={settingsStatus}
        settingsProgressText={settingsProgressText}
        deviceName={tempName}
        setDeviceName={setTempName}
        relayStatuses={latestRelayStatuses}
        relayModes={relayModes}
        onRelayCommand={relayControlsDisabled ? null : handleRelayCommand}
        relayPending={relayPending}
        relayCommandStatus={relayCommandStatus}
        relayControlsDisabled={relayControlsDisabled}
        relayStale={relayVisualStale}
        onSaveDisplayName={saveModalDisplayName}
        nameSavePending={nameSavingModal}
        canRestartController={canRestartController}
        onRestartController={restartController}
        restartPending={restartPending}
        restartStatus={restartStatus}
        notificationDelivery={notificationDelivery}
        notificationActionPending={notificationActionPending}
        onEnableClimateAlerts={enableClimateNotificationsForMe}
      />
    </div>
  );
}
